import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL veya Anon Key bulunamadı!');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helper functions
export const signUp = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });
  return { data, error };
};

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Profile functions
export const createProfile = async (profileData: any) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert([profileData])
    .select()
    .single();
  return { data, error };
};

export const getProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  return { data, error };
};

export const updateProfile = async (userId: string, updates: any) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  return { data, error };
};

// Photo upload function
export const uploadPhoto = async (userId: string, file: File) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    
    // Upload file
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('profile-photos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return { data: null, error: uploadError };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('profile-photos')
      .getPublicUrl(fileName);

    return { data: publicUrl, error: null };
  } catch (err) {
    console.error('Photo upload exception:', err);
    return { data: null, error: err };
  }
};

// Messages functions
export const sendMessage = async (senderId: string, receiverId: string, content: string) => {
  const { data, error } = await supabase
    .from('messages')
    .insert([{ sender_id: senderId, receiver_id: receiverId, content }])
    .select()
    .single();
  return { data, error };
};

export const getMessages = async (userId: string, otherUserId: string) => {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
    .or(`sender_id.eq.${otherUserId},receiver_id.eq.${otherUserId}`)
    .order('created_at', { ascending: true });
  return { data, error };
};

export const markMessageAsRead = async (messageId: string) => {
  const { error } = await supabase
    .from('messages')
    .update({ is_read: true })
    .eq('id', messageId);
  return { error };
};

// Likes and matches
export const likeUser = async (userId: string, likedUserId: string) => {
  const { data, error } = await supabase
    .from('likes')
    .insert([{ user_id: userId, liked_user_id: likedUserId }])
    .select()
    .single();

  // Check if it's a match
  const { data: reverselike } = await supabase
    .from('likes')
    .select('*')
    .eq('user_id', likedUserId)
    .eq('liked_user_id', userId)
    .single();

  if (reverselike) {
    // Create match
    await supabase
      .from('matches')
      .insert([{ user1_id: userId, user2_id: likedUserId }]);
  }

  return { data, error };
};

export const getMatches = async (userId: string) => {
  const { data, error } = await supabase
    .from('matches')
    .select('*, user1:profiles!matches_user1_id_fkey(*), user2:profiles!matches_user2_id_fkey(*)')
    .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);
  return { data, error };
};

// Search users
export const searchUsers = async (filters: any) => {
  let query = supabase.from('profiles').select('*');

  if (filters.gender && filters.gender.length > 0) {
    query = query.in('gender', filters.gender);
  }

  if (filters.country) {
    query = query.eq('country', filters.country);
  }

  if (filters.minAge) {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - filters.minAge);
    query = query.lte('birth_date', maxDate.toISOString().split('T')[0]);
  }

  if (filters.maxAge) {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - filters.maxAge);
    query = query.gte('birth_date', minDate.toISOString().split('T')[0]);
  }

  const { data, error } = await query.limit(50);
  return { data, error };
};

// Ad tracking
export const trackAdView = async (userId: string, adType: string) => {
  const { error } = await supabase
    .from('ad_views')
    .insert([{ user_id: userId, ad_type: adType }]);
  return { error };
};
